/**
 * Navbar.jsx - Global navigation bar.
 *
 * Renders on every authenticated page. Shows the app logo, main nav links,
 * a live "season · week" badge, the model's MAE badge (links to Accuracy
 * page), a demo tour button, and the user avatar with a dropdown menu.
 *
 * On mobile the nav links collapse into a drawer toggled by the hamburger.
 * The MAE badge also appears in the mobile drawer.
 */

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { startTourFn } from '../tourRef';

const Navbar = () => {
  const location        = useLocation();
  const navigate        = useNavigate();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileOpen,  setIsMobileOpen]  = useState(false);

  // User display info — read from localStorage so there's no extra API call
  const [initials,   setInitials]   = useState('?');
  const [firstName,  setFirstName]  = useState('');

  // Prediction week and MAE badges in the nav bar
  const [latestWeek, setLatestWeek] = useState(null);
  const [modelMAE,   setModelMAE]   = useState(null);

  useEffect(() => {
    // Chain: get the latest week, then check if model metrics exist, then load MAE
    api.getAvailableWeeks()
      .then(weeks => {
        if (weeks && weeks.length > 0) {
          const sorted = [...weeks].sort((a, b) =>
            b.season !== a.season ? b.season - a.season : b.week - a.week
          );
          setLatestWeek(sorted[0]);
          return api.getMetricsAvailability();
        }
      })
      .then(avail => {
        if (!avail?.has_data) return;
        const season = avail.seasons_with_metrics?.[0];
        if (!season) return;
        return api.getMetrics(season);
      })
      .then(m => {
        if (m?.holdout_mae) setModelMAE(m.holdout_mae.toFixed(2));
      })
      .catch(() => {});
  }, []);

  // Load user name on mount
  useEffect(() => {
    const fn = localStorage.getItem('user_first_name') || '';
    const ln = localStorage.getItem('user_last_name')  || '';
    setFirstName(fn);
    const i = ((fn[0] || '') + (ln[0] || '')).toUpperCase() || fn.slice(0, 2).toUpperCase() || '?';
    setInitials(i);
  }, []);

  // Re-sync when another tab updates localStorage (e.g. after a profile save)
  useEffect(() => {
    const handler = () => {
      const fn = localStorage.getItem('user_first_name') || '';
      const ln = localStorage.getItem('user_last_name')  || '';
      setFirstName(fn);
      const i = ((fn[0] || '') + (ln[0] || '')).toUpperCase() || fn.slice(0, 2).toUpperCase() || '?';
      setInitials(i);
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const navItems = [
    { label: 'Dashboard',   path: '/dashboard'    },
    { label: 'Analytics',   path: '/analytics'    },
    { label: 'Compare',     path: '/compare'      },
    { label: 'Team Builder', path: '/team-builder' },
  ];

  return (
    <nav className="sticky top-0 z-50 bg-[#050d1f]/95 backdrop-blur-md border-b border-white/[0.10]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between md:grid md:grid-cols-3">

        {/* Logo */}
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2.5 group"
        >
          <div className="w-7 h-7 rounded-lg bg-[#4d7cff] flex items-center justify-center shadow-[0_0_12px_rgba(77,124,255,0.4)]">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <ellipse cx="7" cy="7" rx="5" ry="3.5" stroke="white" strokeWidth="1.2" fill="none"/>
              <line x1="2" y1="7" x2="12" y2="7" stroke="white" strokeWidth="1.2"/>
              <line x1="7" y1="3.5" x2="7" y2="10.5" stroke="white" strokeWidth="0.8"/>
            </svg>
          </div>
          <span className="text-lg font-black tracking-tight text-white group-hover:text-[#4d7cff] transition-colors">
            PlayerIQ
          </span>
        </button>

        {/* Center nav links (desktop only) */}
        <div className="hidden md:flex items-center justify-center bg-white/[0.04] rounded-full p-1 gap-0.5 w-fit mx-auto">
          {navItems.map((item) => {
            const isActive = location.pathname.toLowerCase().startsWith(item.path.toLowerCase());
            return (
              <button
                key={item.label}
                onClick={() => navigate(item.path)}
                className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-[#4d7cff] text-white shadow-[0_2px_8px_rgba(77,124,255,0.35)]'
                    : 'text-white/50 hover:text-white hover:bg-white/5'
                }`}
              >
                {item.label}
              </button>
            );
          })}
        </div>

        {/* Right side: status badges + avatar */}
        <div data-tour="navbar-badges" className="relative flex items-center gap-2 sm:gap-3 justify-end">
          {/* Season/week badge — always rendered to avoid layout shift while loading */}
          <div className="hidden md:flex items-center gap-1.5 bg-white/[0.04] border border-white/[0.08] px-3 py-1.5 rounded-full">
            <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse flex-shrink-0"></div>
            {latestWeek ? (
              <span className="text-xs text-white/40 font-medium whitespace-nowrap">
                {latestWeek.season} · Wk {latestWeek.week}
              </span>
            ) : (
              <span className="w-16 h-3 bg-white/[0.06] rounded animate-pulse inline-block"/>
            )}
          </div>

          {/* MAE badge — clicking navigates to the Accuracy page */}
          <button
            onClick={() => modelMAE && navigate('/accuracy')}
            className="hidden md:flex items-center gap-1.5 bg-white/[0.04] border border-white/[0.08] hover:border-[#4d7cff]/40 hover:bg-[#4d7cff]/5 px-3 py-1.5 rounded-full transition-all"
            style={{ cursor: modelMAE ? 'pointer' : 'default' }}
          >
            <span className="text-[10px] text-white/30 font-medium uppercase tracking-widest">MAE</span>
            {modelMAE ? (
              <span className="text-xs text-[#4d7cff] font-bold whitespace-nowrap">{modelMAE} pts</span>
            ) : (
              <span className="w-10 h-3 bg-white/[0.06] rounded animate-pulse inline-block"/>
            )}
          </button>

          {/* Demo tour trigger */}
          <button
            onClick={() => startTourFn.current?.()}
            className="hidden md:flex items-center gap-1.5 bg-[#4d7cff]/10 border border-[#4d7cff]/30 hover:bg-[#4d7cff]/20 px-3 py-1.5 rounded-full transition-all text-xs font-semibold text-[#4d7cff]"
          >
            ▶ Demo
          </button>

          {/* User avatar — opens profile dropdown */}
          <button
            onClick={() => setIsProfileOpen(!isProfileOpen)}
            className="w-9 h-9 rounded-full bg-gradient-to-br from-[#4d7cff] to-[#3d6cef] flex items-center justify-center font-bold text-xs text-white ring-2 ring-transparent hover:ring-[#4d7cff]/40 transition-all shadow-[0_2px_8px_rgba(77,124,255,0.3)]"
          >
            {initials}
          </button>

          {/* Hamburger menu (mobile) */}
          <button
            onClick={() => setIsMobileOpen(o => !o)}
            className="md:hidden w-9 h-9 flex flex-col items-center justify-center gap-1.5"
          >
            <span className={`block w-5 h-0.5 bg-white/60 transition-all ${isMobileOpen ? 'rotate-45 translate-y-2' : ''}`}/>
            <span className={`block w-5 h-0.5 bg-white/60 transition-all ${isMobileOpen ? 'opacity-0' : ''}`}/>
            <span className={`block w-5 h-0.5 bg-white/60 transition-all ${isMobileOpen ? '-rotate-45 -translate-y-2' : ''}`}/>
          </button>

          {/* Profile dropdown */}
          {isProfileOpen && (
            <>
              {/* Invisible backdrop to close the dropdown on outside click */}
              <div className="fixed inset-0 z-40" onClick={() => setIsProfileOpen(false)} />
              <div className="absolute right-0 top-full mt-2 w-52 bg-[#0c1526] border border-white/[0.08] rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.6)] py-2 z-50 overflow-hidden">
                {/* User name and role */}
                <div className="px-4 py-3 border-b border-white/[0.06]">
                  <div className="text-sm font-semibold text-white truncate">{firstName || 'User'}</div>
                  <div className="text-[10px] text-white/30 uppercase tracking-widest mt-0.5">
                    {localStorage.getItem('user_role') || 'member'}
                  </div>
                </div>
                <div className="py-1">
                  <button
                    onClick={() => { navigate('/profile'); setIsProfileOpen(false); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-white/60 hover:bg-white/5 hover:text-white transition-colors flex items-center gap-2.5"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="7" cy="5" r="2.5"/>
                      <path d="M2 12c0-2.76 2.24-5 5-5s5 2.24 5 5"/>
                    </svg>
                    My Profile
                  </button>
                  <button
                    onClick={() => { navigate('/dashboard'); setIsProfileOpen(false); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-white/60 hover:bg-white/5 hover:text-white transition-colors flex items-center gap-2.5"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="1.5" y="1.5" width="4" height="4" rx="1"/>
                      <rect x="8.5" y="1.5" width="4" height="4" rx="1"/>
                      <rect x="1.5" y="8.5" width="4" height="4" rx="1"/>
                      <rect x="8.5" y="8.5" width="4" height="4" rx="1"/>
                    </svg>
                    Dashboard
                  </button>
                </div>
                <div className="border-t border-white/[0.06] py-1">
                  <button
                    onClick={() => { navigate('/help'); setIsProfileOpen(false); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-white/60 hover:bg-white/5 hover:text-white transition-colors flex items-center gap-2.5"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="7" cy="7" r="5.5"/>
                      <path d="M7 10v-.5"/>
                      <path d="M7 8c0-1.5 2-1.5 2-3a2 2 0 0 0-4 0"/>
                    </svg>
                    Help & FAQ
                  </button>
                </div>
                <div className="border-t border-white/[0.06] py-1">
                  <button
                    onClick={() => { navigate('/logout'); setIsProfileOpen(false); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/5 transition-colors flex items-center gap-2.5"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M5 2H3a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h2"/>
                      <polyline points="9 10 12 7 9 4"/>
                      <line x1="12" y1="7" x2="5" y2="7"/>
                    </svg>
                    Log Out
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Mobile drawer */}
      {isMobileOpen && (
        <div className="md:hidden border-t border-white/[0.08] bg-[#050d1f]/98 px-4 py-3 flex flex-col gap-1">

          {/* Status badges row */}
          <div className="flex gap-2 px-1 pb-2">
            <div className="flex items-center gap-1.5 bg-white/[0.04] border border-white/[0.08] px-3 py-1.5 rounded-full flex-1 justify-center">
              <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse flex-shrink-0"/>
              {latestWeek ? (
                <span className="text-xs text-white/50 font-medium">{latestWeek.season} · Wk {latestWeek.week}</span>
              ) : (
                <span className="w-16 h-3 bg-white/[0.06] rounded animate-pulse inline-block"/>
              )}
            </div>
            <button
              onClick={() => { modelMAE && navigate('/accuracy'); setIsMobileOpen(false); }}
              className="flex items-center gap-1.5 bg-white/[0.04] border border-white/[0.08] px-3 py-1.5 rounded-full flex-1 justify-center"
            >
              <span className="text-[10px] text-white/30 font-medium uppercase tracking-widest">MAE</span>
              {modelMAE ? (
                <span className="text-xs text-[#4d7cff] font-bold">{modelMAE} pts</span>
              ) : (
                <span className="w-10 h-3 bg-white/[0.06] rounded animate-pulse inline-block"/>
              )}
            </button>
          </div>

          {navItems.map((item) => {
            const isActive = location.pathname.toLowerCase().startsWith(item.path.toLowerCase());
            return (
              <button
                key={item.label}
                onClick={() => { navigate(item.path); setIsMobileOpen(false); }}
                className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  isActive ? 'bg-[#4d7cff]/15 text-[#4d7cff]' : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                {item.label}
              </button>
            );
          })}
          <div className="border-t border-white/[0.06] mt-1 pt-2">
            <button
              onClick={() => { navigate('/profile'); setIsMobileOpen(false); }}
              className="w-full text-left px-4 py-3 rounded-xl text-sm text-white/60 hover:text-white hover:bg-white/5 transition-colors"
            >
              My Profile
            </button>
            <button
              onClick={() => { navigate('/logout'); setIsMobileOpen(false); }}
              className="w-full text-left px-4 py-3 rounded-xl text-sm text-red-400 hover:bg-red-500/5 transition-colors"
            >
              Log Out
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
